// Donor-portal landing/summary API helper.
//
// Backend: portal.py canonical at GET /api/v1/my-giving/summary
// (PortalGivingSummary schema) — THE landing payload for v1 + v2 +
// mobile (CHG-2946, Slice E / program §8 decision 5).
//
// CHG-2946 retarget notes:
//   - The interface here is named PortalGivingSummary to match the live
//     backend schema (src/api/app/schemas/portal.py). The old local name
//     PortalMeOverview mirrored a backend schema that was never wired to
//     any route and was deleted by CHG-2946.
//   - The summary now INLINES the landing-enrichment fields this file
//     used to document as "LOST": next_recurring_charge (date + amount +
//     currency + org teaser), pledge_committed_per_currency /
//     pledge_remaining_per_currency (PER-CURRENCY maps — never
//     cross-currency sums), and recent_donations (top-5, refund-aware
//     per CHG-2934). MyOverview.tsx consumes them directly — recent
//     gifts are NOT fetched from /my-giving/donations.
//
// PortalDonationRead is also exported here because it's referenced
// from me-donations.ts (which doesn't define its own copy to avoid
// type drift).

import { apiClient } from "./client";

export interface PortalCurrencyTotal {
  currency: string;
  total_minor_units: number;
  count: number;
}

export interface PortalDonationRead {
  id: string;
  organization_id: string;
  org_name: string | null;
  org_logo_url: string | null;
  designation_id: string | null;
  designation_name: string | null;
  campaign_id: string | null;
  session_id: string | null;
  session_name: string | null;
  amount_minor_units: number;
  currency: string;
  channel: string | null;
  status: string;
  donor_name: string | null;
  anonymous: boolean;
  notes: string | null;
  paid_at: string | null;
  created_at: string;
  // CHG-2934 (§8 decision 6 — EXPOSE) — refund signal. amount_minor_units
  // stays GROSS; refunded_amount_minor_units is the cumulative refunded
  // total and effective_amount_minor_units the NET, so list/detail amounts
  // reconcile with the net /my-giving/summary lifetime figure. Optional —
  // fall back to amount_minor_units when absent.
  refunded_amount_minor_units?: number;
  effective_amount_minor_units?: number | null;
}

/** CHG-2946 (Slice E) — the donor's soonest upcoming recurring charge
 *  (MIN(next_charge_at) across active recurring donations). Null on the
 *  summary when nothing is scheduled. */
export interface PortalNextRecurringCharge {
  next_charge_at: string;
  amount_minor_units: number;
  currency: string;
  organization_id: string;
  org_name: string | null;
}

/** Mirrors backend PortalGivingSummary (src/api/app/schemas/portal.py). */
export interface PortalGivingSummary {
  annual_total_minor_units: number;
  annual_donation_count: number;
  lifetime_total_minor_units: number;
  lifetime_donation_count: number;
  orgs_supported: number;
  active_recurring_count: number;
  open_pledges_count: number;
  last_donation_at: string | null;
  /** Single-currency donors get a real ISO code; mixed-currency donors get null. */
  currency: string | null;
  annual_per_currency: PortalCurrencyTotal[];
  lifetime_per_currency: PortalCurrencyTotal[];
  // CHG-2946 (Slice E) — landing-enrichment fields. Optional so the
  // helper tolerates a pre-enrichment backend during rollout.
  next_recurring_charge?: PortalNextRecurringCharge | null;
  /** Open-pledge committed totals, one entry per currency (never summed across). */
  pledge_committed_per_currency?: PortalCurrencyTotal[];
  /** Open-pledge remaining (committed − fulfilled, clamped at 0) per currency. */
  pledge_remaining_per_currency?: PortalCurrencyTotal[];
  /** 5 most-recent succeeded donations, refund-aware (CHG-2934 fields). */
  recent_donations?: PortalDonationRead[];
}

export async function fetchMyOverview(): Promise<PortalGivingSummary> {
  const r = await apiClient().get<PortalGivingSummary>("/my-giving/summary");
  return r.data;
}

export const meOverviewApi = {
  fetchMyOverview,
};
